import { useState, useEffect, useCallback } from 'react';
import { Heart, Loader2, Send, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCheckinAlerts() {
  const [data, setData] = useState(null);
  const [hours, setHours] = useState(24);
  const [loading, setLoading] = useState(true);
  const [broadcast, setBroadcast] = useState({ titolo: '', messaggio: '' });
  const [sending, setSending] = useState(false);

  const token = localStorage.getItem('lifelink_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/checkin-alerts`, { params: { hours }, headers });
      setData(resp.data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const sendBroadcast = async () => {
    if (!broadcast.titolo || !broadcast.messaggio) {
      toast.error('Inserisci titolo e messaggio');
      return;
    }
    setSending(true);
    try {
      const resp = await axios.post(`${API}/admin/notifications/broadcast`, null, {
        params: { titolo: broadcast.titolo, messaggio: broadcast.messaggio },
        headers,
      });
      toast.success(resp.data.message || 'Notifica inviata');
      setBroadcast({ titolo: '', messaggio: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio');
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-checkin">
      {/* Broadcast */}
      <div className="border border-[#B8D4E8] bg-white p-4" data-testid="broadcast-form">
        <div className="flex items-center gap-2 mb-3">
          <Send size={14} className="text-[#FF3B30]" />
          <h3 className="text-xs font-bold uppercase tracking-widest text-[#0D1B2A]">Notifica Broadcast a Tutti</h3>
        </div>
        <div className="space-y-3">
          <input
            type="text" placeholder="Titolo notifica" value={broadcast.titolo}
            onChange={(e) => setBroadcast({ ...broadcast, titolo: e.target.value })}
            className="w-full bg-[#F0F7FC] border border-[#B8D4E8] text-[#0D1B2A] px-3 py-2 text-sm focus:border-[#FF3B30] focus:outline-none"
            data-testid="broadcast-titolo"
          />
          <textarea
            placeholder="Messaggio..." value={broadcast.messaggio} rows={3}
            onChange={(e) => setBroadcast({ ...broadcast, messaggio: e.target.value })}
            className="w-full bg-[#F0F7FC] border border-[#B8D4E8] text-[#0D1B2A] px-3 py-2 text-sm focus:border-[#FF3B30] focus:outline-none resize-none"
            data-testid="broadcast-messaggio"
          />
          <button
            onClick={sendBroadcast}
            disabled={sending}
            className="flex items-center gap-2 px-4 py-2 bg-[#FF3B30] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#FF1A1A] disabled:opacity-40"
            data-testid="send-broadcast-btn"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Invia a Tutti
          </button>
        </div>
      </div>

      {/* Checkin alerts */}
      <div className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Heart size={16} className="text-[#FF3B30]" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#0D1B2A]">Utenti senza Check-in recente</span>
          </div>
          <select
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="bg-white border border-[#B8D4E8] text-[#0D1B2A] p-2 text-xs uppercase tracking-widest focus:border-[#FF3B30] focus:outline-none"
            data-testid="checkin-hours-filter"
          >
            <option value={6}>6 ore</option>
            <option value={12}>12 ore</option>
            <option value={24}>24 ore</option>
            <option value={48}>48 ore</option>
            <option value={72}>72 ore</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-8"><Loader2 size={24} className="animate-spin inline text-[#4A6B8A]" /></div>
        ) : !data || data.utenti_senza_checkin.length === 0 ? (
          <div className="text-center py-12 border border-[#B8D4E8] bg-white">
            <Heart size={48} className="mx-auto text-[#32D74B] mb-3" />
            <p className="text-sm text-[#4A6B8A]">Tutti gli utenti hanno effettuato il check-in</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 px-3 py-2 bg-[#FF9F0A]/10 border border-[#FF9F0A]/30">
              <AlertTriangle size={14} className="text-[#FF9F0A]" />
              <span className="text-xs text-[#FF9F0A] font-bold">
                {data.utenti_senza_checkin.length} utenti non hanno effettuato check-in nelle ultime {data.soglia_ore} ore
              </span>
            </div>
            <div className="border border-[#B8D4E8] bg-white overflow-hidden">
              <table className="w-full" data-testid="checkin-table">
                <thead>
                  <tr className="bg-[#E8F4FD]">
                    <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Nome</th>
                    <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Email</th>
                    <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Telefono</th>
                    <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Ultimo Check-in</th>
                  </tr>
                </thead>
                <tbody>
                  {data.utenti_senza_checkin.map((u, i) => (
                    <tr key={i} className="border-t border-[#E8F4FD] hover:bg-[#F0F8FF]">
                      <td className="px-4 py-3 text-sm text-[#0D1B2A] font-bold">{u.nome} {u.cognome}</td>
                      <td className="px-4 py-3 text-xs text-[#4A6B8A]">{u.email}</td>
                      <td className="px-4 py-3 text-xs text-[#4A6B8A]">{u.telefono || '-'}</td>
                      <td className="px-4 py-3 text-[10px] font-mono text-[#6B8AB5]">
                        {u.ultimo_checkin ? new Date(u.ultimo_checkin).toLocaleString('it-IT') : 'MAI'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
